import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Check, Package, Tag } from "lucide-react";
import axios from "axios";

const API = `/api`;

const isLightColor = (hex) => {
  if (!hex || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
};

const stockConfig = {
  in_stock: { bg: "bg-green-100", text: "text-green-800", label: "In Stock" },
  limited: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Limited Stock" },
  out_of_stock: { bg: "bg-red-100", text: "text-red-800", label: "Out of Stock" }
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const res = await axios.get(`${API}/groups/${id}`);
        setGroup(res.data);
        if (res.data.variants?.length > 0) {
          setSelectedVariant(res.data.variants[0]);
        }
      } catch {
        // Error fetching group
      } finally {
        setLoading(false);
      }
    };
    fetchGroup();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen py-12 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="animate-pulse">
            <div className="h-6 bg-[#E6E4DD] w-32 mb-12" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="aspect-square bg-[#E6E4DD]" />
              <div className="space-y-6">
                <div className="h-12 bg-[#E6E4DD] w-3/4" />
                <div className="h-24 bg-[#E6E4DD] w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-['Cormorant_Garamond'] text-4xl text-[#1C1C1A] mb-6">Product Not Found</h1>
          <Link to="/products" className="inline-flex items-center gap-2 text-[#4A5D4E] hover:gap-3 transition-all">
            <ArrowLeft size={18} /> Back to Collections
          </Link>
        </div>
      </div>
    );
  }

  const variants = group.variants || [];
  const totalRolls = variants.reduce((sum, v) => sum + (v.stock_rolls || 0), 0);

  return (
    <div className="min-h-screen" data-testid="product-detail-page">
      <Helmet>
        <title>{`${group.name} | DUGARS Collections`}</title>
        <meta name="description" content={`${group.name} fabric collection by DUGARS. ${variants.length} colour options available. ${(group.description || '').replace(/\n/g, ' ')}`} />
        <link rel="canonical" href={`https://thedugars.in/products/${id}`} />
        <meta property="og:title" content={`${group.name} | DUGARS Collections`} />
        <meta property="og:description" content={`${group.name} — ${variants.length} colours available. ${(group.description || 'Premium wholesale fabrics.').replace(/\n/g, ' ')}`} />
        <meta property="og:url" content={`https://thedugars.in/products/${id}`} />
        <meta property="og:type" content="product" />
        {group.image_url && <meta property="og:image" content={`https://thedugars.in${group.image_url}`} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${group.name} | DUGARS Collections`} />
        <meta name="twitter:description" content={`${group.name} — ${variants.length} colours available.`} />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-white py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-[#6B6A65] hover:text-[#1C1C1A] transition-colors" data-testid="back-to-products">
            <ArrowLeft size={16} /> Back to Collections
          </Link>
        </div>
      </div>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left — Color display area */}
            <div>
              <div className="aspect-square overflow-hidden bg-[#F7F5F0] flex items-center justify-center" data-testid="color-display">
                {selectedVariant?.image_url ? (
                  <img src={selectedVariant.image_url} alt={selectedVariant.color_name} className="w-full h-full object-cover" />
                ) : selectedVariant?.hex_code ? (
                  <div className="w-full h-full" style={{ backgroundColor: selectedVariant.hex_code }}>
                    <div className="w-full h-full flex items-end p-8">
                      <span className={`text-lg font-medium ${isLightColor(selectedVariant.hex_code) ? 'text-[#1C1C1A]/60' : 'text-white/60'}`}>
                        {selectedVariant.color_name}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-[#6B6A65]">
                    <Package size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Select a colour below</p>
                  </div>
                )}
              </div>

              {/* Selected variant details */}
              {selectedVariant && (
                <div className="mt-6 p-4 bg-[#F7F5F0] border border-[#E6E4DD]" data-testid="selected-variant-info">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#1C1C1A]">{selectedVariant.color_name}</p>
                      {selectedVariant.lot_no && (
                        <p className="text-xs text-[#6B6A65] mt-0.5">Lot: {selectedVariant.lot_no}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {(selectedVariant.discount_percent || selectedVariant.discount_label) && (
                        <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700">
                          {selectedVariant.discount_percent ? `${selectedVariant.discount_percent}% OFF` : selectedVariant.discount_label}
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium ${(stockConfig[selectedVariant.stock_status] || stockConfig.in_stock).bg} ${(stockConfig[selectedVariant.stock_status] || stockConfig.in_stock).text}`}>
                        {selectedVariant.stock_rolls > 0
                          ? `${selectedVariant.stock_rolls} Roll${selectedVariant.stock_rolls !== 1 ? 's' : ''} (${selectedVariant.stock_rolls * 30} kg)`
                          : 'Out of Stock'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right — Info + Color grid */}
            <div className="flex flex-col">
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-normal tracking-tight text-[#1C1C1A] mb-4" data-testid="product-name">
                {group.name}
              </h1>
              {group.description && (
                <p className="text-[#6B6A65] font-light leading-relaxed mb-6">{group.description}</p>
              )}

              {/* Stats */}
              <div className="flex gap-8 mb-8 py-4 border-y border-[#E6E4DD]">
                <div>
                  <p className="text-2xl font-light text-[#1C1C1A]">{variants.length}</p>
                  <p className="text-xs text-[#6B6A65] uppercase tracking-wider">Colours</p>
                </div>
                <div>
                  <p className="text-2xl font-light text-[#1C1C1A]">30 kg</p>
                  <p className="text-xs text-[#6B6A65] uppercase tracking-wider">Per Roll</p>
                </div>
              </div>

              {/* Color Grid */}
              {variants.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-sm font-medium uppercase tracking-wide text-[#1C1C1A] mb-4">
                    Available Colours ({variants.length})
                  </h2>
                  <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 gap-3" data-testid="color-grid">
                    {variants.map((variant) => {
                      const isSelected = selectedVariant?.id === variant.id;
                      const hasDiscount = variant.discount_percent || variant.discount_label;
                      return (
                        <button
                          key={variant.id}
                          onClick={() => setSelectedVariant(variant)}
                          className={`relative group/swatch flex flex-col items-center`}
                          title={`${variant.color_name}${variant.lot_no ? ` (${variant.lot_no})` : ''}`}
                          data-testid={`color-swatch-${variant.id}`}
                        >
                          <div className={`w-full aspect-square rounded-sm border-2 transition-all overflow-hidden
                            ${isSelected ? 'border-[#1C1C1A] ring-2 ring-[#4A5D4E]/30 scale-105' : 'border-transparent hover:border-[#4A5D4E]/50 hover:scale-105'}
                            ${variant.stock_status === 'out_of_stock' ? 'opacity-40' : ''}`}>
                            {variant.image_url ? (
                              <img src={variant.image_url} alt={variant.color_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full" style={{ backgroundColor: variant.hex_code || '#ccc' }}>
                                {isSelected && (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Check size={16} className={isLightColor(variant.hex_code) ? 'text-[#1C1C1A]' : 'text-white'} />
                                  </div>
                                )}
                              </div>
                            )}
                            {hasDiscount && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                <Tag size={8} className="text-white" />
                              </div>
                            )}
                          </div>
                          <span className="mt-1 text-[10px] text-[#6B6A65] leading-tight text-center truncate w-full">
                            {variant.color_name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* All variants table */}
              {variants.length > 0 && (
                <div className="border border-[#E6E4DD] overflow-hidden mb-8">
                  <div className="bg-[#1C1C1A] px-4 py-3">
                    <h3 className="text-sm font-medium text-[#F7F5F0] uppercase tracking-wider">Stock Details</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F7F5F0] sticky top-0">
                        <tr>
                          <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6A65] uppercase">Colour</th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6A65] uppercase">Lot No.</th>
                          <th className="text-center px-4 py-2 text-xs font-medium text-[#6B6A65] uppercase">Rolls</th>
                          <th className="text-center px-4 py-2 text-xs font-medium text-[#6B6A65] uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {variants.map((v) => {
                          const sc = stockConfig[v.stock_status] || stockConfig.in_stock;
                          return (
                            <tr
                              key={v.id}
                              className={`border-t border-[#E6E4DD] cursor-pointer hover:bg-[#F7F5F0]/50 transition-colors ${selectedVariant?.id === v.id ? 'bg-[#4A5D4E]/5' : ''}`}
                              onClick={() => setSelectedVariant(v)}
                              data-testid={`variant-row-${v.id}`}
                            >
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-sm border border-[#E6E4DD] flex-shrink-0" style={{ backgroundColor: v.hex_code || '#ccc' }} />
                                  <span className="text-[#1C1C1A]">{v.color_name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-[#6B6A65]">{v.lot_no || '-'}</td>
                              <td className="px-4 py-2.5 text-center text-[#1C1C1A] font-medium">{v.stock_rolls}</td>
                              <td className="px-4 py-2.5 text-center">
                                <span className={`px-2 py-0.5 text-xs ${sc.bg} ${sc.text}`}>{sc.label}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="mt-auto pt-4">
                <Link
                  to={`/contact?product=${encodeURIComponent(group.name)}${selectedVariant ? `&color=${encodeURIComponent(selectedVariant.color_name)}` : ''}`}
                  className="btn-primary w-full text-center block"
                  data-testid="request-quote-btn"
                >
                  Request Quote
                </Link>
                <p className="text-center text-sm text-[#6B6A65] mt-4">
                  Or call us at{" "}
                  <a href="tel:+916360372623" className="text-[#4A5D4E] hover:underline">+91 6360372623</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;
